import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from 'store';
import { CheckBox } from 'components/CheckBox';
import TableLoader from 'components/TableLoader';
import { SelectComponent } from '../../../SelectComponent';
import { IOptionsInterface } from '../../../TableSelect/types';
import {
  setFarmersKpiChosenAssessment,
  setFarmerKpiChosenDrillDownCategory,
  setFarmersKpiDrillDownChosenAssessment,
  setFarmerKpiDrillDownSelectedParts, setFarmerFilterSpecies, getFarmerKpi,
} from 'store/farmerKpi/actions';
import { labelsForGraph, labelsForGraphDrill } from '../FarmerKpiGeneralTab/config';
import { GraphDataModel } from '../FarmerKpiGeneralTab/types';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getPerformanceIndicatorByValue } from '../FarmerKpiGeneralTab/config';
import { useTranslation } from "react-i18next";

interface IGraphInterface {
  data: Array<GraphDataModel>;
  heading: string;
}

export const renderCustomizedLabel = props => {
  const { x, y, height, value } = props;
  const yPos = isNaN(y) ? height - 2 : y + height - 2;
  return (
    <text
      x={x + 465}
      y={y + height - 2}
      fill="#3e3e3f"
      textAnchor="end"
      style={{ fontSize: '14px' }}
    >
      {value}
    </text>
  );
};

const FarmerKpiDrillDown = () => {
  const dispatch = useDispatch();
  const {
    isLoading,
    drillDownSelectedParts,
    dataListDrill,
    filteredSpecies
  } = useSelector((state: IAppState) => state.farmerKpi);
  const [currentAssessment, setCurrentAssessment] = useState<any>({});
  const { t, i18n } = useTranslation();

  const onCategoryChange = (category:any) => {
    dispatch(setFarmerKpiChosenDrillDownCategory(category));
    dispatch(getFarmerKpi({SpeciesFilterValues: [category.value]}));
    dispatch(setFarmerFilterSpecies(category.value))
  };

  const assessmentOptions = useMemo(() => {
    let options = [];
    if (dataListDrill.Assessments) {
      for (let key in dataListDrill.Assessments) {
        options.push({
          value: key,
          label: `${dataListDrill.Assessments[key].Name} ${new Date(dataListDrill.Assessments[key].AssessmentDate)
            .toString()
            .substring(4, 15)}`,
        });
      };
    }
    return options;
  }, [dataListDrill]);

  const onAssessmentChoose = (ass: any) => {
    dispatch(setFarmersKpiDrillDownChosenAssessment(ass));
    setCurrentAssessment(ass)
  };
  useEffect(()=>{
    if(filteredSpecies && currentAssessment.value) {
      const searchData = {
        SpeciesFilterValues: [filteredSpecies],
        Assessments: [+currentAssessment.value]
      }
      dispatch(getFarmerKpi(searchData));
    }
  },[filteredSpecies, currentAssessment])

  const assessmentPartFiltered = dataListDrill?.KpiDataModels?.map(ass => {
    return {...ass.AssessmentParts[0], assessmentName: ass?.Name}
  }) || [];
  const checkboxOptions = assessmentPartFiltered.map(assPart => {
    return {
    Name: assPart.Name,
    KPI_ID: assPart.KPI_ID
    }
  })
  useEffect(()=>{
  if(checkboxOptions.length){
    dispatch(setFarmerKpiDrillDownSelectedParts(checkboxOptions?.[0]?.KPI_ID));
    dispatch(setFarmerKpiDrillDownSelectedParts(checkboxOptions?.[1]?.KPI_ID));
    dispatch(setFarmerKpiDrillDownSelectedParts(checkboxOptions?.[2]?.KPI_ID));
  }
  },[dataListDrill])

  const dataForGraphs: Array<IGraphInterface> = drillDownSelectedParts.map(
    part => {
      let dataToShow = [];
    if(assessmentPartFiltered?.length) {
      const assPart = assessmentPartFiltered?.find(p => p.KPI_ID === part);
      if (assPart?.Values?.length > 0 && assPart?.Values?.[0]) {
        for (const [key, value] of Object.entries(assPart.Values[0])) {
          if(key === 'Value') {
            dataToShow = [
              ...dataToShow,
              {
                name: assPart?.assessmentName,
                value: Number(value),
              },
            ];
          };
          if(key === 'PreviousAssessment' && value) {
            dataToShow = [
              ...dataToShow,
              {
                name: `Previous Assessment`,
                value: Number(value),
              },
            ];
          };
          if (labelsForGraphDrill[key]) {
            dataToShow = [
              ...dataToShow,
              {
                name: `${labelsForGraphDrill[key]}`,
                value: Number(value),
              },
            ];
          }
        }
      }
      return {
        heading: `${assPart?.Name}${assPart?.UnitPostFix ? ', ' + assPart?.UnitPostFix : ''}`,
        data: dataToShow,
      };
    }
    },
  );

  const onChangeKpiValue = (kpiId: number) => {
    dispatch(setFarmerKpiDrillDownSelectedParts(kpiId));
  };

  return (
    <div
      className="tab-pane"
      id="drill-down"
      role="tabpanel"
      aria-labelledby="certificates-tab"
    >
      {isLoading ? (
        <TableLoader />
      ) : (
        <>
          <div className="chart-filter">
            <div className="kpi-row kpi-row-md">
              <div className="kpi-item">
                <div className="form-group">
                  <SelectComponent
                    isMulti={false}
                    options={dataListDrill?.SpeciesFilterValues?.map(c => ({ value: c, label: c }))}
                    label={t('RM_spec')}
                    onChange={onCategoryChange}
                    value={filteredSpecies}
                  />
                </div>
              </div>
              <div className="kpi-item">
                <div className="form-group">
                  <SelectComponent
                    isMulti={false}
                    options={assessmentOptions}
                    label={t('RM_ass')}
                    onChange={onAssessmentChoose}
                    value={currentAssessment?.value}
                  />
                </div>
              </div>
            </div>
            <div className="filter-checkboxes">
              <div className="checkboxes-row">
                {filteredSpecies && currentAssessment.value && checkboxOptions.map(assPart => (
                    <CheckBox
                      key={assPart.KPI_ID}
                      isSelected={drillDownSelectedParts?.some(
                        p => p === assPart.KPI_ID,
                      )}
                      label={assPart.Name}
                      onChange={() => {
                        onChangeKpiValue(assPart.KPI_ID);
                      }}
                      name={`${assPart.Name.replace(' ', '')}${assPart.KPI_ID}`}
                    />
                  ))}
              </div>
            </div>
          </div>
          {filteredSpecies && currentAssessment.value && <div className="chart-wrapper">
            <div className="drilldown-charts">
              {dataForGraphs?.length > 0 &&
              dataForGraphs.map(data => {
                if (data?.heading !== 'undefined') return ((
                  <div className="drilldown-chart" key={data.heading}>
                    <h2 className="chart-heading heading-small">
                      {data.heading}
                    </h2>
                    <ResponsiveContainer
                      width="100%"
                      height={data.data.length * 46}
                    >
                      <BarChart
                        layout="vertical"
                        data={data.data}
                        margin={{
                          top: 5,
                          right: 30,
                          left: 20,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid
                          strokeDasharray="500 1"
                          horizontal={false}
                          vertical={false}
                        />
                        <XAxis type="number" />
                        <YAxis
                          dataKey="name"
                          type="category"
                          scale="band"
                          width={90}
                        />
                        <Tooltip />
                        <Bar
                          dataKey="value"
                          barSize={14}
                          radius={[10, 10, 10, 10]}
                          background={{ fill: '#edf0f1' }}
                        >
                          {data.data.map(d => (
                            <Cell
                              fill={
                                '#37a041'
                              }
                              key={d.name}
                            />
                          ))}
                          <LabelList
                            dataKey="value"
                            position="right"
                            width={100}
                            style={{ fill: '#000' }}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ))
              })}
            </div>
          </div>}
        </>
      )}
    </div>
  );
};

export { FarmerKpiDrillDown };
